/** @jsx jsx */
import React, { useCallback, useEffect, useMemo } from 'react';
import { jsx } from '@compiled/react';
import type { JiraProjectType } from '@jira/platform__project-types/src/project-types.tsx';
import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { CustomLogo, AppLogo } from '@atlaskit/navigation-system';
import { useIsFhsEnabled } from '@atlaskit/navigation-system/fhs-rollout';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { testIdGenerate } from '@atlassian/jira-navigation-apps-common/src/utils/test-id.tsx';
import { useProductName } from '@atlassian/jira-tenant-context-controller/src/components/product-name/index.tsx';
import { useRouterActions } from '@atlassian/react-resource-router';
import { useApplicationPermissions } from '@atlassian/jira-tenant-context-controller/src/components/application-permissions/index.tsx';
import { useSetCustomThemeData } from '@atlassian/jira-navigation-apps-common/src/controllers/custom-theme-context/index.tsx';
import { NAVIGATION_ITEM_ID } from '../../common/constants.tsx';
import { TopLevelErrorBoundary } from '../../common/ui/error-boundary/index.tsx';
import { useCustomLogo, useCustomIcon } from '../../controllers/custom-logo/index.tsx';
import { useNavigationItemAnalytics } from '../../controllers/navigation-item-analytics/main.tsx';
import messages from './messages.tsx';
import { JSMAndCSMLogo } from './jsm-and-csm-logo/index.tsx';
import { getProductIcon, getLogoLabel } from './utils.tsx';

const HOME_URL = '/jira';

type Props = {
	projectType?: JiraProjectType | null | undefined;
};

const CreateCustomLogoComponent = (props: { alt: string; src: string; testId: string }) => () => (
	<CustomImgNav4 {...props} />
);

export const ProductHome = (props: Props): JSX.Element => (
	<TopLevelErrorBoundary id={NAVIGATION_ITEM_ID.HOME}>
		<ProductHomeContent {...props} />
	</TopLevelErrorBoundary>
);

const styles = cssMap({
	logoContainer: {
		display: 'contents',
	},
});

function ProductHomeContent({ projectType }: Props) {
	const isFhsEnabled = fg('navx-2731-convert-jira-fhs-rollout')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useIsFhsEnabled()
		: fg('navx-full-height-sidebar');

	const testIdPrefix = testIdGenerate('product-home-');
	const logoUrl = useCustomLogo();
	const iconUrl = useCustomIcon();
	const productName = useProductName();
	const triggerAnalytics = useNavigationItemAnalytics({
		navigationItemId: NAVIGATION_ITEM_ID.LOGO,
	});

	// See: https://hello.jira.atlassian.cloud/browse/NAVX-1993
	const setCustomThemeData = useSetCustomThemeData();
	if (fg('jfp-magma-fix-custom-theme-data-hydration-error')) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		useEffect(() => {
			setCustomThemeData({
				hasCustomLogo: !!logoUrl,
			});
		}, [logoUrl, setCustomThemeData]);
	} else {
		setCustomThemeData({
			hasCustomLogo: !!logoUrl,
		});
	}

	const { formatMessage } = useIntl();
	const { push } = useRouterActions();
	const navigateToHome = useCallback(
		// @ts-expect-error - TS7006 - Parameter 'ev' implicitly has an 'any' type.
		(ev) => {
			triggerAnalytics();
			if (ev.ctrlKey || ev.metaKey) return;
			ev.preventDefault();
			push(HOME_URL);
		},
		[triggerAnalytics, push],
	);
	const { icon: ProductIcon, name: logoName } = useMemo(
		() => getProductIcon(productName, testIdPrefix),
		[productName, testIdPrefix],
	);

	const Logo = useMemo(
		() =>
			CreateCustomLogoComponent({
				alt: formatMessage(messages.customLogoAltText),
				src: logoUrl || '',
				testId: `${testIdPrefix}-logo`,
			}),
		[logoUrl, formatMessage, testIdPrefix],
	);

	const Icon = useMemo(
		() =>
			logoUrl && iconUrl
				? () => (
						<CustomImgNav4
							alt={formatMessage(messages.customIconAltText)}
							src={iconUrl}
							testId={`${testIdPrefix}-icon`}
						/>
					)
				: ProductIcon,
		[logoUrl, iconUrl, ProductIcon, testIdPrefix, formatMessage],
	);

	const label = getLogoLabel(formatMessage, productName);

	const {
		hasSoftwareAccess,
		hasServiceDeskAccess,
		hasCustomerServiceAccess,
		hasProductDiscoveryAccess,
	} = useApplicationPermissions();

	// check if the user has access to JSM and CSM only
	const isJSMAndCSMOnlyAccessible =
		hasServiceDeskAccess &&
		hasCustomerServiceAccess &&
		!hasSoftwareAccess &&
		!hasProductDiscoveryAccess;

	if (!logoUrl && isJSMAndCSMOnlyAccessible && fg('team25-eu-jira-logo-updates-csm-jsm')) {
		return (
			<Box testId={`${testIdPrefix}-container`} xcss={isFhsEnabled && styles.logoContainer}>
				<JSMAndCSMLogo
					navigateToHome={navigateToHome}
					testIdPrefix={testIdPrefix}
					homeURL={HOME_URL}
					projectType={projectType}
				/>
			</Box>
		);
	}

	return (
		<Box testId={`${testIdPrefix}-container`} xcss={isFhsEnabled && styles.logoContainer}>
			{!logoUrl ? (
				<AppLogo
					name={logoName}
					label={label}
					icon={Icon}
					href={HOME_URL}
					onClick={navigateToHome}
				/>
			) : (
				// Render custom logo component if logoUrl is present (even if the icon is not custom)
				<CustomLogo
					label={label}
					icon={Icon}
					logo={Logo}
					href={HOME_URL}
					onClick={navigateToHome}
				/>
			)}
		</Box>
	);
}

type CustomImgProps = {
	alt: string;
	src: string;
	testId: string;
};

function CustomImgNav4({ alt, src, testId }: CustomImgProps) {
	// See: https://hello.jira.atlassian.cloud/browse/NAVX-1993
	const setCustomThemeData = useSetCustomThemeData();
	const handleLoad = useCallback(
		(e: React.SyntheticEvent<HTMLImageElement>) => {
			// Image loaded
			setCustomThemeData({
				customLogoSize: {
					width: e.currentTarget.naturalWidth,
					height: e.currentTarget.naturalHeight,
				},
			});
		},
		[setCustomThemeData],
	);

	return <img src={src} alt={alt} data-testid={testId} onLoad={handleLoad} />;
}
